/** Verified sources: docs/operations/clause-verifications/compliance-only-duties-2026-09-30.md. */
/**
 * SIRS (Serious Incident Response Scheme) notification clocks.
 *
 * The reporting deadline itself is computed and stamped on the `incidents`
 * row elsewhere (at incident write time, via `determineSirsPriority` /
 * `calculateSirsDeadline` in `../index.ts`) — this module does not
 * re-derive it, it classifies how urgent an already-known deadline is.
 */
import { calculateSirsFinalReportDeadline } from '../legacy-core';
import type { ComplianceDeadlineItem } from './types';
import type { ComplianceDeadlineSettings } from '../compliance-settings';

const HREF = (incidentId: string) => `/compliance/incidents/${incidentId}`;
const MS_PER_HOUR = 1000 * 60 * 60;
const MS_PER_DAY = MS_PER_HOUR * 24;

/** A dashboard countdown uses the recorded, correctable legal deadline.
 * Missing or invalid source evidence stays unavailable; creation time cannot
 * stand in for the provider's awareness fact. */
export function remainingUntilSirsDeadline(
  reportingDeadline: string | null | undefined,
  now: Date,
  unit: 'hours' | 'days',
): number | null {
  if (!reportingDeadline) return null;
  const deadline = Date.parse(reportingDeadline);
  if (!Number.isFinite(deadline)) return null;
  const delta = (deadline - now.getTime()) / (unit === 'hours' ? MS_PER_HOUR : MS_PER_DAY);
  if (delta < 0) return -Math.max(1, Math.round(Math.abs(delta)));
  return Math.round(delta);
}

export type SirsPriorityOneWindow = 't2' | 't4' | 't12';

export interface SirsPriorityOneRow {
  id: string;
  reportingDeadline: string;
}

export interface SirsPriorityOneEvaluation {
  item: ComplianceDeadlineItem;
  window: SirsPriorityOneWindow;
  hoursRemaining: number;
}

/**
 * SIRS Priority 1 — notify within 24 hours of becoming aware
 * (Aged Care Rules 2025 r 165A-25(1)).
 *
 * Cadence windows T-12h / T-4h / T-2h; most-urgent match wins so a single
 * evaluation never reports more than one window for the same incident.
 * Returns null when the incident is not inside any reminder window.
 */
export function evaluateSirsPriorityOneApproaching(
  row: SirsPriorityOneRow,
  now: Date,
  settings?: ComplianceDeadlineSettings,
): SirsPriorityOneEvaluation | null {
  const deadlineMs = new Date(row.reportingDeadline).getTime();
  const hoursRemaining = Math.max(0, (deadlineMs - now.getTime()) / MS_PER_HOUR);
  const t2 = settings?.sirs_priority_1_t2_reminder_lead ?? 2;
  const t4 = settings?.sirs_priority_1_t4_reminder_lead ?? 4;
  const t12 = settings?.sirs_priority_1_t12_reminder_lead ?? 12;
  const window: SirsPriorityOneWindow | null =
    hoursRemaining <= t2
      ? 't2'
      : hoursRemaining <= t4
        ? 't4'
        : hoursRemaining <= t12
          ? 't12'
          : null;
  if (!window) return null;
  return {
    window,
    hoursRemaining,
    item: {
      id: `sirs-p1:${row.id}:${window}`,
      register: 'SIRS_NOTIFICATION',
      recordRef: row.id,
      title: 'SIRS Priority 1 notification',
      dueAt: row.reportingDeadline,
      kind: 'STATUTORY',
      provisionOrTargetLabel: 'Aged Care Rules 2025 r 165A-25(1)',
      status: 'DUE_SOON',
      href: HREF(row.id),
      // The T-2 P1 window is a verified final statutory window. Escalation
      // consumes this explicit marker; it does not guess final windows from
      // generic urgency labels or move the reporting deadline.
      meta: { window, hoursRemaining, verifiedFinalWindow: window === 't2' },
    },
  };
}

export interface SirsPriorityTwoRow {
  id: string;
  reportingDeadline: string;
}

export interface SirsPriorityTwoEvaluation {
  item: ComplianceDeadlineItem;
  daysRemaining: number;
}

/**
 * SIRS Priority 2 — notify within 30 days of becoming aware
 * (Aged Care Rules 2025 r 165A-30(1)).
 */
export function evaluateSirsPriorityTwoApproaching(
  row: SirsPriorityTwoRow,
  now: Date,
): SirsPriorityTwoEvaluation {
  const deadlineMs = new Date(row.reportingDeadline).getTime();
  const daysRemaining = Math.max(0, (deadlineMs - now.getTime()) / MS_PER_DAY);
  return {
    daysRemaining,
    item: {
      id: `sirs-p2:${row.id}`,
      register: 'SIRS_NOTIFICATION',
      recordRef: row.id,
      title: 'SIRS Priority 2 notification',
      dueAt: row.reportingDeadline,
      kind: 'STATUTORY',
      provisionOrTargetLabel: 'Aged Care Rules 2025 r 165A-30(1)',
      status: 'DUE_SOON',
      href: HREF(row.id),
      meta: { daysRemaining },
    },
  };
}

export interface SirsMissedRow {
  id: string;
  reportingDeadline: string;
  /** Optional — only present when the row selects sirs_effective_priority. */
  sirsPriority?: 1 | 2 | null;
}

/**
 * SIRS notification deadline MISSED — reporting_deadline has passed and the
 * Commission still has not been notified. Matches whichever priority the
 * incident carries; falls back to the general r 165A-25 citation when the
 * caller did not resolve a priority for the row.
 */
export function evaluateSirsMissed(
  row: SirsMissedRow,
  now: Date,
): { item: ComplianceDeadlineItem } | null {
  const deadlineMs = new Date(row.reportingDeadline).getTime();
  if (deadlineMs >= now.getTime()) return null;
  const provision =
    row.sirsPriority === 1
      ? 'Aged Care Rules 2025 r 165A-25(1)'
      : row.sirsPriority === 2
        ? 'Aged Care Rules 2025 r 165A-30(1)'
        : 'Aged Care Rules 2025 rr 165A-25(1), 165A-30(1)';
  return {
    item: {
      id: `sirs-missed:${row.id}`,
      register: 'SIRS_NOTIFICATION',
      recordRef: row.id,
      title: 'SIRS notification deadline missed',
      dueAt: row.reportingDeadline,
      kind: 'STATUTORY',
      provisionOrTargetLabel: provision,
      status: 'MISSED',
      href: HREF(row.id),
      meta: {},
    },
  };
}

export interface SirsFinalReportRow {
  id: string;
  /** Already resolved: final_report_deadline_override ?? final_report_deadline. */
  effectiveDeadline: string;
}

export interface SirsFinalReportEvaluation {
  item: ComplianceDeadlineItem;
  daysRemaining: number;
}

/**
 * SIRS final report — 84 days from the initial notice, when the Commissioner
 * requests one (Aged Care Rules 2025 r 165A-45(1), (2)(a), (2)(b)). The
 * Commissioner-specified override (2)(b) is resolved by the caller before
 * this function runs; this function only classifies urgency.
 */
export function evaluateSirsFinalReportApproaching(
  row: SirsFinalReportRow,
  now: Date,
): SirsFinalReportEvaluation {
  const deadlineMs = new Date(row.effectiveDeadline).getTime();
  const daysRemaining = Math.max(0, (deadlineMs - now.getTime()) / MS_PER_DAY);
  return {
    daysRemaining,
    item: {
      id: `sirs-final-report:${row.id}`,
      register: 'SIRS_NOTIFICATION',
      recordRef: row.id,
      title: 'SIRS final report',
      dueAt: row.effectiveDeadline,
      kind: 'STATUTORY',
      provisionOrTargetLabel: 'Aged Care Rules 2025 r 165A-45(1), (2)(a), (2)(b)',
      status: deadlineMs < now.getTime() ? 'OVERDUE' : 'DUE_SOON',
      href: HREF(row.id),
      meta: { daysRemaining },
    },
  };
}

/** Shared read projection while a governed first-notice reader awaits executor work.
 * The mutable commission_notification_date and stored derived deadline are never
 * proof of the first qualifying notice. A Commissioner-specified deadline stands
 * independently; otherwise callers must supply explicit first-notice evidence.
 * No attempt's REJECTED/ACCEPTED status alone determines legal qualification.
 * Verified: docs/operations/clause-verifications/compliance-only-duties-2026-09-30.md.
 */
export function effectiveSirsFinalReportDueAt(
  row: {
    commission_notification_date?: string | null;
    final_report_required?: boolean | null;
    final_report_deadline_override?: string | null;
    final_report_deadline?: string | null;
    sirs_first_notice_given_at?: string | null;
    sirs_first_notice_evidence_reference?: string | null;
  },
  timezone: string,
  firstNoticeEvidence?: { givenAt: string; evidenceReference: string },
): string | null {
  if (!row.final_report_required) return null;
  if (row.final_report_deadline_override) return row.final_report_deadline_override;
  const governedEvidence =
    firstNoticeEvidence ??
    (row.sirs_first_notice_given_at && row.sirs_first_notice_evidence_reference
      ? {
          givenAt: row.sirs_first_notice_given_at,
          evidenceReference: row.sirs_first_notice_evidence_reference,
        }
      : undefined);
  if (
    !governedEvidence?.evidenceReference.trim() ||
    !/(Z|[+-]\d{2}:\d{2})$/.test(governedEvidence.givenAt)
  )
    return null;
  return (
    calculateSirsFinalReportDeadline(governedEvidence.givenAt, true, timezone)?.toISOString() ??
    null
  );
}

/** Reminder clocks preserve legacy work without certifying a first notice. */
export function sirsFinalReportReminder(
  row: Parameters<typeof effectiveSirsFinalReportDueAt>[0],
  timezone: string,
  now: Date,
): { dueAt: string; anchorReviewRequired: boolean } | null {
  if (!row.final_report_required) return null;
  const governed = effectiveSirsFinalReportDueAt(row, timezone);
  if (governed) return { dueAt: governed, anchorReviewRequired: false };
  const stored = row.final_report_deadline;
  const storedMs = stored ? Date.parse(stored) : NaN;
  const legacy = Number.isFinite(storedMs)
    ? new Date(storedMs).toISOString()
    : row.commission_notification_date &&
        Number.isFinite(Date.parse(row.commission_notification_date))
      ? calculateSirsFinalReportDeadline(
          row.commission_notification_date,
          true,
          timezone,
        )?.toISOString()
      : null;
  return { dueAt: legacy ?? now.toISOString(), anchorReviewRequired: true };
}
